import DOMPurify from 'dompurify';

export const MAIL_PREFS_KEY = 'mailPreferences';

function prefsCacheKey(userKey) {
  const id = String(userKey || '').trim().toLowerCase();
  return id ? `${MAIL_PREFS_KEY}:${id}` : MAIL_PREFS_KEY;
}

export const DEFAULT_MAIL_PREFERENCES = {
  blockExternalContent: true,
  replyQuoteOriginal: true,
  trackEmailOpens: true,
  trackingBaseUrl: '',
  /** Unter empfangener Mail: Verlauf der an diesen Absender gesendeten Mails */
  showInboxSentHistory: true,
  /** Sekunden bis Versand / endgültiges Löschen; 0 = sofort */
  undoDelaySeconds: 10,
  /** Windows-/OS-Toast bei neuer Inbox-Mail (Browser muss laufen) */
  desktopNotifications: true,
  /** Erinnerungen vor Kalenderterminen */
  calendarReminders: true,
  /** Minuten vor Termin-Start (Mehrfachauswahl möglich) */
  calendarReminderMinutes: [15],
  firstContactAutoReply: {
    enabled: false,
    subject: '',
    message: '',
  },
  vacation: {
    enabled: false,
    subject: 'Abwesend / Out of Office',
    message: '',
    /** YYYY-MM-DD — nach diesem Tag Abwesenheit (+ Weiterleitung) aus */
    until: '',
  },
  forward: {
    enabled: false,
    to: '',
  },
};

const ALLOWED_CALENDAR_REMINDER_MINUTES = [0, 5, 10, 15, 30, 60, 120, 1440];

/** Normalisiert „Abwesend bis“ auf YYYY-MM-DD oder leer. */
export function normalizeVacationUntil(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return '';
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return '';
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** Ende des Abwesenheitstags (lokal), oder null. */
export function vacationUntilEndDate(until) {
  const day = normalizeVacationUntil(until);
  if (!day) return null;
  const [y, mo, d] = day.split('-').map(Number);
  return new Date(y, mo - 1, d, 23, 59, 59, 999);
}

/** true, wenn „bis“ gesetzt ist und der Tag vorbei ist. */
export function isAbsencePeriodEnded(until, now = new Date()) {
  const end = vacationUntilEndDate(until);
  if (!end) return false;
  return now.getTime() > end.getTime();
}

export const CALENDAR_REMINDER_OPTIONS = [
  { value: 0, label: 'Bei Beginn' },
  { value: 5, label: '5 Min.' },
  { value: 10, label: '10 Min.' },
  { value: 15, label: '15 Min.' },
  { value: 30, label: '30 Min.' },
  { value: 60, label: '1 Std.' },
  { value: 120, label: '2 Std.' },
  { value: 1440, label: '1 Tag' },
];

export function normalizeCalendarReminderMinutes(raw) {
  const list = Array.isArray(raw)
    ? raw
    : raw != null && raw !== ''
      ? [raw]
      : DEFAULT_MAIL_PREFERENCES.calendarReminderMinutes;
  const cleaned = [
    ...new Set(
      list
        .map((v) => parseInt(v, 10))
        .filter((n) => Number.isFinite(n) && ALLOWED_CALENDAR_REMINDER_MINUTES.includes(n))
    ),
  ].sort((a, b) => a - b);
  return cleaned.length ? cleaned : [...DEFAULT_MAIL_PREFERENCES.calendarReminderMinutes];
}

export function normalizeMailPreferences(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const first = src.firstContactAutoReply || {};
  const vacation = src.vacation || {};
  const forward = src.forward || {};

  const undoRaw = parseInt(
    src.undoDelaySeconds ?? src.sendUndoDelaySeconds,
    10
  );
  return {
    blockExternalContent: src.blockExternalContent !== false,
    replyQuoteOriginal: src.replyQuoteOriginal !== false,
    trackEmailOpens: src.trackEmailOpens !== false,
    trackingBaseUrl: String(src.trackingBaseUrl || '').trim(),
    showInboxSentHistory: src.showInboxSentHistory !== false,
    undoDelaySeconds: Number.isFinite(undoRaw)
      ? Math.min(60, Math.max(0, undoRaw))
      : DEFAULT_MAIL_PREFERENCES.undoDelaySeconds,
    desktopNotifications: src.desktopNotifications !== false,
    calendarReminders: src.calendarReminders !== false,
    calendarReminderMinutes: normalizeCalendarReminderMinutes(src.calendarReminderMinutes),
    firstContactAutoReply: {
      enabled: !!first.enabled,
      subject: String(first.subject || ''),
      message: String(first.message || ''),
    },
    vacation: {
      enabled: !!vacation.enabled,
      subject: String(vacation.subject || DEFAULT_MAIL_PREFERENCES.vacation.subject),
      message: String(vacation.message || ''),
      until: normalizeVacationUntil(vacation.until),
    },
    forward: {
      enabled: !!forward.enabled,
      to: String(forward.to || '').trim(),
    },
  };
}

export function readCachedMailPreferences(userKey = '') {
  try {
    const raw =
      localStorage.getItem(prefsCacheKey(userKey)) ||
      (!userKey ? localStorage.getItem(MAIL_PREFS_KEY) : null);
    if (!raw) return { ...DEFAULT_MAIL_PREFERENCES };
    return normalizeMailPreferences(JSON.parse(raw));
  } catch (_) {
    return { ...DEFAULT_MAIL_PREFERENCES };
  }
}

export function cacheMailPreferences(prefs, userKey = '') {
  const normalized = normalizeMailPreferences(prefs);
  localStorage.setItem(prefsCacheKey(userKey), JSON.stringify(normalized));
  return normalized;
}

/** Externe CSS/Hintergrund-URLs in Styles entfernen (Tracker), Farben behalten. */
function neutralizeExternalStyleAssets(html = '') {
  return String(html || '')
    .replace(/@import\b[^;]+;/gi, '')
    .replace(/url\s*\(\s*(['"]?)[^)]*\1\s*\)/gi, 'none')
    .replace(/\bbackground-image\s*:\s*none\s*;?/gi, '');
}

/**
 * E-Mail-<style>-Blöcke unter .email-html-frame scopen,
 * damit Absender-Klassen (Tipico .fc1 etc.) greifen ohne die App zu überschreiben.
 */
function scopeCssSelectors(css, prefix = '.email-html-frame') {
  const src = String(css || '').replace(/\/\*[\s\S]*?\*\//g, '');
  const out = [];
  let i = 0;

  const skipWs = () => {
    while (i < src.length && /\s/.test(src[i])) i += 1;
  };

  const readSelectors = () => {
    const start = i;
    while (i < src.length && src[i] !== '{') i += 1;
    return src.slice(start, i).trim();
  };

  const readDeclarationBlock = () => {
    if (src[i] !== '{') return '';
    i += 1;
    const start = i;
    let depth = 1;
    while (i < src.length && depth > 0) {
      if (src[i] === '{') depth += 1;
      else if (src[i] === '}') depth -= 1;
      i += 1;
    }
    return src.slice(start, i - 1);
  };

  const scopeSelectorList = (selectors) =>
    selectors
      .split(',')
      .map((raw) => {
        let sel = raw.trim();
        if (!sel) return sel;
        if (/^(from|to|\d+%)$/i.test(sel)) return sel;
        if (sel.startsWith(prefix)) return sel;
        if (/^(html|body|:root)$/i.test(sel)) return prefix;
        sel = sel.replace(/^(html|body|:root)(?=[\s.#[:>+~]|$)/i, prefix);
        if (sel.startsWith(prefix)) return sel;
        return `${prefix} ${sel}`;
      })
      .join(', ');

  const parseRules = () => {
    while (i < src.length) {
      skipWs();
      if (i >= src.length || src[i] === '}') {
        if (src[i] === '}') i += 1;
        return;
      }

      if (src[i] === '@') {
        const atStart = i;
        while (i < src.length && src[i] !== '{' && src[i] !== ';') i += 1;
        const header = src.slice(atStart, i).trim();

        if (src[i] === ';') {
          i += 1;
          // @import etc. weglassen
          continue;
        }
        if (src[i] !== '{') break;

        if (/^@(media|supports|layer|container)/i.test(header)) {
          out.push(`${header}{`);
          i += 1;
          parseRules();
          out.push('}');
          continue;
        }

        // @keyframes / @font-face unverändert übernehmen
        const block = readDeclarationBlock();
        out.push(`${header}{${block}}`);
        continue;
      }

      const selectors = readSelectors();
      if (!selectors || src[i] !== '{') break;
      const body = readDeclarationBlock();
      out.push(`${scopeSelectorList(selectors)}{${body}}`);
    }
  };

  parseRules();
  return out.join('\n');
}

/**
 * Dark-Mode-Anpassungen aus Absender-CSS entfernen.
 * Die treffen in der App oft nur die Schrift (hell), lassen den weißen
 * Hintergrund aber stehen → unleserlich; Invertieren macht es dann noch schlimmer.
 */
function stripDarkColorSchemeMedia(css = '') {
  const src = String(css || '');
  let out = '';
  let i = 0;

  while (i < src.length) {
    if (src[i] !== '@') {
      out += src[i];
      i += 1;
      continue;
    }

    const atStart = i;
    while (i < src.length && src[i] !== '{' && src[i] !== ';') i += 1;
    const header = src.slice(atStart, i).trim();

    if (src[i] === ';') {
      out += src.slice(atStart, i + 1);
      i += 1;
      continue;
    }

    if (src[i] !== '{') {
      out += src.slice(atStart, i);
      continue;
    }

    const isDarkSchemeMedia =
      /^@media\b/i.test(header) &&
      /prefers-color-scheme\s*:\s*dark/i.test(header) &&
      !/prefers-color-scheme\s*:\s*light/i.test(header);

    // Block inkl. geschachtelter Klammern konsumieren
    let depth = 0;
    while (i < src.length) {
      if (src[i] === '{') depth += 1;
      else if (src[i] === '}') {
        depth -= 1;
        if (depth === 0) {
          i += 1;
          break;
        }
      }
      i += 1;
    }

    if (!isDarkSchemeMedia) {
      out += src.slice(atStart, i);
    }
  }

  return out;
}

function scopeEmailStyleTags(html = '') {
  return String(html || '').replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_, attrs, css) => {
    const cleanAttrs = String(attrs || '').replace(/\s*type\s*=\s*["'][^"']*["']/gi, '');
    const cleanedCss = stripDarkColorSchemeMedia(css);
    return `<style${cleanAttrs}>\n${scopeCssSelectors(cleanedCss)}\n</style>`;
  });
}

/** DOMPurify-Config für eingehende HTML-Mails */
export function sanitizeEmailHtml(html, { allowExternalImages = false } = {}) {
  void html;
  const config = {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['style', 'font', 'center'],
    ADD_ATTR: [
      'target',
      'style',
      'bgcolor',
      'background',
      'color',
      'align',
      'valign',
      'height',
      'width',
      'border',
      'cellpadding',
      'cellspacing',
      'face',
      'size',
      'role',
    ],
  };

  if (!allowExternalImages) {
    config.FORBID_TAGS = ['img', 'svg', 'object', 'iframe', 'link'];
    config.FORBID_ATTR = ['srcset'];
  } else {
    config.FORBID_TAGS = ['link'];
  }

  return config;
}

/**
 * Open-Tracking-Pixel aus HTML entfernen (Anzeige + Zitat):
 * eigenes /api/t/… sowie gängige fremde Pixel-Muster.
 */
export function stripOpenTrackingPixels(html = '') {
  let out = String(html || '');
  out = out.replace(/<img\b[^>]*\/api\/t\/[^>]*>/gi, '');
  out = out.replace(
    /<img\b[^>]*src=["'][^"']*(?:\/(?:pixel|open|track|tracking|spacer|blank|beacon|1x1|dot|clear)\.(?:gif|png|jpe?g)(?:\?[^"']*)?|(?:pixel|open|track)\.gif(?:\?[^"']*)?)["'][^>]*>/gi,
    ''
  );
  out = out.replace(
    /<img\b(?=[^>]*\b(?:width|height)\s*=\s*["']?1["']?)[^>]*src=["'][^"']*(?:track|pixel|open|beacon|\/t\/|click|utm_)[^"']*["'][^>]*>/gi,
    ''
  );
  return out;
}

function unwrapClickHref(href = '') {
  const raw = String(href || '').replace(/&amp;/gi, '&').trim();
  try {
    const parsed = new URL(raw);
    if (!/\/api\/c\/[a-f0-9]{16,}/i.test(parsed.pathname)) return raw;
    const dest = parsed.searchParams.get('u');
    return dest ? decodeURIComponent(dest) : raw;
  } catch (_) {
    return raw;
  }
}

/** Click-Tracker in Anzeige/Zitat auf Original-URL zurücksetzen. */
export function unwrapClickTrackingUrls(html = '') {
  return String(html || '').replace(
    /\bhref\s*=\s*(["'])([^"']*)\1/gi,
    (full, quote, href) => {
      const original = unwrapClickHref(href);
      if (original === href.replace(/&amp;/gi, '&')) return full;
      return `href=${quote}${original.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}${quote}`;
    }
  );
}

/** Tracking-URLs aus Plaintext (Antwort-Zitat) entfernen. */
export function stripTrackingUrlsFromText(text = '') {
  return String(text || '')
    .replace(/https?:\/\/[^\s<>"']+\/api\/c\/[a-f0-9]+(?:\?[^\s<>"']*)?/gi, (url) => {
      try {
        const dest = new URL(url).searchParams.get('u');
        if (dest) return decodeURIComponent(dest);
      } catch (_) {
        /* drop */
      }
      return '';
    })
    .replace(/https?:\/\/[^\s<>"']+\/api\/t\/[^\s<>"']*/gi, '')
    .replace(
      /https?:\/\/[^\s<>"']+\/(?:pixel|open|track|tracking|spacer|blank|beacon|1x1|dot|clear)\.(?:gif|png)(?:\?[^\s<>"']*)?/gi,
      ''
    )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
}

/** HTML für Anzeige in der App aufbereiten */
export function prepareEmailHtmlForDisplay(html = '', sanitizeOptions = {}) {
  let cleaned = unwrapClickTrackingUrls(stripOpenTrackingPixels(html));
  cleaned = scopeEmailStyleTags(cleaned);
  if (!sanitizeOptions.allowExternalImages) {
    cleaned = neutralizeExternalStyleAssets(cleaned);
  }
  return {
    html: cleaned,
    purifyConfig: sanitizeEmailHtml(cleaned, sanitizeOptions),
  };
}

export function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function htmlToPlainText(html = '') {
  return String(html)
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|tr|li|h[1-6]|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function hostnameFromUrl(url) {
  try {
    return new URL(String(url || '')).hostname.replace(/^www\./i, '');
  } catch (_) {
    return '';
  }
}

function linkLabelFromHref(href, alt = '') {
  const hint = String(alt || '').trim();
  if (hint && !/^cid:/i.test(hint) && !/^image\d+/i.test(hint)) return hint;
  const raw = String(href || '');
  if (/facebook\.com/i.test(raw)) return 'Facebook';
  if (/instagram\.com/i.test(raw)) return 'Instagram';
  if (/linkedin\.com/i.test(raw)) return 'LinkedIn';
  if (/youtube\.com|youtu\.be/i.test(raw)) return 'YouTube';
  if (/\bxing\.com/i.test(raw)) return 'Xing';
  if (/(?:twitter\.com|\bx\.com)\//i.test(raw)) return 'X';
  if (/tiktok\.com/i.test(raw)) return 'TikTok';
  return hostnameFromUrl(raw) || raw;
}

/** Outlook/IMAP: [cid:image001.png@…] und cid:-URLs aus Klartext entfernen. */
export function stripCidArtifactsFromText(text = '') {
  return String(text || '')
    .replace(/\[cid:[^\]]+\]\s*<?(https?:\/\/[^\s<>\]]+)>?/gi, '$1')
    .replace(/\(cid:[^)]+\)/gi, '')
    .replace(/\[cid:[^\]]+\]/gi, '')
    .replace(/<cid:[^>]+>/gi, '')
    .replace(/\bcid:[^\s<>"'\[\]]+/gi, '')
    .replace(/<(https?:\/\/[^>]+)>/gi, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Inline-Bilder (cid:) in Links durch den Ziel-Link ersetzen,
 * übrige cid:-Bilder entfernen — sonst landet „[cid:image001.png@…]“ im Zitat.
 */
export function rewriteCidReferencesInHtml(html = '') {
  let out = String(html || '');
  out = out.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs, inner) => {
    const hrefMatch = String(attrs || '').match(/href\s*=\s*["']([^"']+)["']/i);
    const href = hrefMatch ? String(hrefMatch[1] || '').replace(/&amp;/gi, '&') : '';
    const hasCid =
      /src\s*=\s*["']\s*cid:/i.test(inner) ||
      /href\s*=\s*["']\s*cid:/i.test(String(attrs || ''));
    if (!hasCid) return full;
    if (/^https?:\/\//i.test(href)) {
      const altMatch = String(inner).match(/alt\s*=\s*["']([^"']*)["']/i);
      return `<a href="${escapeHtml(href)}">${escapeHtml(linkLabelFromHref(href, altMatch?.[1]))}</a>`;
    }
    return String(inner).replace(/<img\b[^>]*src\s*=\s*["']\s*cid:[^"']+["'][^>]*>/gi, '');
  });
  out = out.replace(/<img\b[^>]*src\s*=\s*["']\s*cid:[^"']+["'][^>]*>/gi, '');
  out = out.replace(/<v:imagedata\b[^>]*\/?>/gi, '');
  return out;
}

function quoteBodyFromEmail(email) {
  const htmlRaw = String(email?.html || '').trim();
  if (htmlRaw) {
    const rewritten = rewriteCidReferencesInHtml(
      unwrapClickTrackingUrls(stripOpenTrackingPixels(htmlRaw))
    );
    const cleaned = rewritten.replace(/\s+/g, ' ').trim();
    if (cleaned && /<[a-z][\s\S]*>/i.test(cleaned)) {
      const safe = DOMPurify.sanitize(
        rewritten,
        sanitizeEmailHtml(rewritten, { allowExternalImages: true })
      );
      return `<div class="email-quote-html">${safe}</div>`;
    }
  }
  const rawPlain = (email.text && String(email.text).trim())
    ? String(email.text).trim()
    : htmlToPlainText(unwrapClickTrackingUrls(stripOpenTrackingPixels(email.html || '')));
  const plain = stripCidArtifactsFromText(stripTrackingUrlsFromText(rawPlain));
  return plainTextToQuotedParagraphs(plain);
}

export function isEmptyRichText(html = '') {
  return !String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u200b/g, '')
    .trim();
}

function plainTextToQuotedParagraphs(text = '') {
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  if (!lines.length) return '<p><br></p>';
  return lines
    .map((line) => {
      if (!line.trim()) return '<p><br></p>';
      return `<p style="margin:0 0 0.35em;white-space:pre-wrap;">${escapeHtml(line)}</p>`;
    })
    .join('');
}

/** Sauberes Zitat für Anzeige + Versand (nicht in Quill editieren). */
export function buildOriginalQuoteHtml(email, { formatDate, mode = 'reply' } = {}) {
  if (!email) return '';

  const dateStr = typeof formatDate === 'function'
    ? formatDate(email.date)
    : (email.date || '');
  const bodyHtml = quoteBodyFromEmail(email);

  // Inline-Farben bleiben hell für externe Mail-Clients; App-Dark-Mode überschreibt via CSS.
  const boxStyle =
    'margin-top:18px;padding:12px 14px;border-left:3px solid #a3a3a3;background:#fafafa;color:#404040;font-size:13px;line-height:1.55;border-radius:0 8px 8px 0;';
  const metaStyle = 'margin:0 0 4px;font-size:12px;color:#737373;';
  const headerStyle = 'margin:0 0 10px;font-size:12px;color:#737373;';

  if (mode === 'forward') {
    return (
      `<div class="email-quote email-quote-box" style="${boxStyle}">` +
      `<p class="email-quote-meta" style="margin:0 0 10px;font-size:12px;font-weight:600;color:#737373;">---------- Weitergeleitete Nachricht ----------</p>` +
      `<p class="email-quote-meta" style="${metaStyle}"><strong>Von:</strong> ${escapeHtml(email.from || '')}</p>` +
      `<p class="email-quote-meta" style="${metaStyle}"><strong>An:</strong> ${escapeHtml(email.to || '')}</p>` +
      `<p class="email-quote-meta" style="${metaStyle}"><strong>Datum:</strong> ${escapeHtml(dateStr)}</p>` +
      `<p class="email-quote-meta" style="margin:0 0 12px;font-size:12px;color:#737373;"><strong>Betreff:</strong> ${escapeHtml(email.subject || '')}</p>` +
      bodyHtml +
      `</div>`
    );
  }

  const header = dateStr
    ? `Am ${dateStr} schrieb ${email.from || 'Unbekannt'}:`
    : `${email.from || 'Unbekannt'} schrieb:`;

  return (
    `<div class="email-quote email-quote-box" style="${boxStyle}">` +
    `<p class="email-quote-meta" style="${headerStyle}">${escapeHtml(header)}</p>` +
    bodyHtml +
    `</div>`
  );
}

export function combineReplyAndQuote(replyHtml = '', quoteHtml = '') {
  const reply = String(replyHtml || '').trim() || '<p><br></p>';
  const quote = String(quoteHtml || '').trim();
  if (!quote) return reply;
  return `${reply}${quote}`;
}

/** Kompatibilität: Antwort + Zitat in einem HTML-String */
export function buildQuotedReplyHtml(email, { quoteOriginal = true, formatDate, mode = 'reply' } = {}) {
  if (!quoteOriginal) return '<p><br></p>';
  return `<p><br></p>${buildOriginalQuoteHtml(email, { formatDate, mode })}`;
}
